import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import styles from '../css-isolated/consult_followup.module.css';
import authFetch from '../utils/authFetch';

function ConsultFollowup({ hideSystemHeader = false, selectedPDL, isDarkMode = true, initialDate = null }) {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [followUpConsultations, setFollowUpConsultations] = useState([]);
  const [selectedDateConsultations, setSelectedDateConsultations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPDLForConfirm, setSelectedPDLForConfirm] = useState(null);
  const [showSelectionSuccessModal, setShowSelectionSuccessModal] = useState(false);
  const [selectionSuccessData, setSelectionSuccessData] = useState({ name: '', message: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [consultationToDelete, setConsultationToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [consultationToEdit, setConsultationToEdit] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editMonth, setEditMonth] = useState('');
  const [editDay, setEditDay] = useState('');
  const [editYear, setEditYear] = useState('');
  const [editDateError, setEditDateError] = useState('');

  // Fetch follow-up consultations
  useEffect(() => {
    fetchFollowUpConsultations();
  }, []);

  // When an initialDate is provided (e.g. navigated from notification), auto-select
  // that date once consultations have been loaded.
  useEffect(() => {
    if (!initialDate || followUpConsultations.length === 0) return;
    const target = new Date(initialDate);
    target.setHours(0, 0, 0, 0);
    // Navigate calendar to the correct month
    setCurrentDate(new Date(target.getFullYear(), target.getMonth(), 1));
    // Highlight the date
    setSelectedDate(target);
    // Populate the Scheduled Consultations panel
    const dateStr = target.toISOString().split('T')[0];
    const consultations = followUpConsultations.filter(consult => {
      const consultDate = new Date(consult.date).toISOString().split('T')[0];
      return consultDate === dateStr;
    });
    setSelectedDateConsultations(consultations);
  }, [initialDate, followUpConsultations]);

  const fetchFollowUpConsultations = async () => {
    try {
      setIsLoading(true);
      const response = await authFetch(`${API_BASE_URL}/api/followup-consult`);
      if (response.ok) {
        const data = await response.json();
        setFollowUpConsultations(data);
      }
    } catch (error) {
      console.error('Error fetching follow-up consultations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get consultations count for a specific date
  const getConsultationsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return followUpConsultations.filter(consult => {
      const consultDate = new Date(consult.date).toISOString().split('T')[0];
      return consultDate === dateStr;
    });
  };

  // Get count of consultations for a date
  const getConsultationCount = (date) => {
    return getConsultationsForDate(date).length;
  };

  // Get month name and year
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  
  const currentMonth = monthNames[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();

  // Get the first day of the month
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  
  // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayWeekday = firstDayOfMonth.getDay();
  
  // Get total days in month
  const daysInMonth = lastDayOfMonth.getDate();
  
  // Get days from previous month to fill the first week
  const prevMonthLastDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
  const prevMonthDays = firstDayWeekday;
  
  // Calculate total cells needed (always show 6 weeks for consistency)
  const totalCells = 42;
  
  // Generate calendar days
  const calendarDays = [];
  
  // Previous month days
  for (let i = prevMonthDays - 1; i >= 0; i--) {
    calendarDays.push({
      day: prevMonthLastDay - i,
      isCurrentMonth: false,
      isPrevMonth: true,
      date: new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, prevMonthLastDay - i)
    });
  }
  
  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      day: i,
      isCurrentMonth: true,
      isPrevMonth: false,
      isNextMonth: false,
      date: new Date(currentDate.getFullYear(), currentDate.getMonth(), i)
    });
  }
  
  // Next month days to fill remaining cells
  const remainingCells = totalCells - calendarDays.length;
  for (let i = 1; i <= remainingCells; i++) {
    calendarDays.push({
      day: i,
      isCurrentMonth: false,
      isNextMonth: true,
      date: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i)
    });
  }

  // Check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Check if a date is selected
  const isSelected = (date) => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Handle date selection
  const handleDateClick = (dayObj) => {
    setSelectedDate(dayObj.date);
    const consultations = getConsultationsForDate(dayObj.date);
    setSelectedDateConsultations(consultations);
  };

  // Go to today
  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Handle selecting a PDL from consultation
  const handleSelectPDL = (consultation) => {
    if (consultation.hr_info) {
      const followUpDate = consultation.date || null;
      const consultationDate = followUpDate ? new Date(followUpDate) : null;
      const today = new Date();

      if (consultationDate) {
        consultationDate.setHours(0, 0, 0, 0);
      }
      today.setHours(0, 0, 0, 0);

      // Show warning only for upcoming follow-up dates.
      const hasUpcomingDateWarning = consultationDate ? consultationDate > today : false;

      // Store the entire consultation object to access the ID/date later.
      setSelectedPDLForConfirm({
        ...consultation.hr_info,
        followUpConsultationId: consultation.id,
        followUpConsultationDate: followUpDate,
        hasUpcomingDateWarning
      });
      setShowConfirmModal(true);
    }
  };

  const formatFollowUpDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    return new Date(dateValue).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Confirm PDL selection
  const confirmSelection = () => {
    if (selectedPDLForConfirm) {
      const pdlData = {
        id: selectedPDLForConfirm.id,
        lastName: selectedPDLForConfirm.last_name,
        firstName: selectedPDLForConfirm.first_name,
        middleName: selectedPDLForConfirm.middle_name,
        aka: selectedPDLForConfirm.alias_nickname || selectedPDLForConfirm.aka,
        fullName: `${selectedPDLForConfirm.last_name || ''}, ${selectedPDLForConfirm.first_name || ''} ${selectedPDLForConfirm.middle_name || ''}`.trim(),
        alsoKnownAs: selectedPDLForConfirm.alias_nickname || selectedPDLForConfirm.aka || 'N/A',
        sex: selectedPDLForConfirm.sex,
        age: calculateAge(selectedPDLForConfirm.date_of_birth),
        civilStatus: selectedPDLForConfirm.civil_status,
        currentCell: selectedPDLForConfirm.cell ? selectedPDLForConfirm.cell.cell_no : 'Unassigned',
        cell: selectedPDLForConfirm.cell ? {
          id: selectedPDLForConfirm.cell.id,
          cell_no: selectedPDLForConfirm.cell.cell_no
        } : null,
        photo: selectedPDLForConfirm.photo_of_pdl,
        followUpConsultationId: selectedPDLForConfirm.followUpConsultationId // Add the follow-up consultation ID
      };
      
      localStorage.setItem('selectedPDL', JSON.stringify(pdlData));
      
      setShowConfirmModal(false);
      setSelectionSuccessData({
        name: `${selectedPDLForConfirm.last_name}, ${selectedPDLForConfirm.first_name}`,
        message: 'PDL has been selected successfully. You can now proceed with the consultation.'
      });
      setShowSelectionSuccessModal(true);
    }
  };

  // Cancel selection
  const cancelSelection = () => {
    setShowConfirmModal(false);
    setSelectedPDLForConfirm(null);
  };

  // Handle success modal close and navigation
  const handleSuccessModalClose = () => {
    setShowSelectionSuccessModal(false);
    // Navigate to health consultation wizard with flag to show symptoms tab
    navigate('/health-consultation', { state: { autoShowSymptoms: true } });
  };

  // Handle delete consultation
  const handleDeleteConsultation = (consultation) => {
    setConsultationToDelete(consultation);
    setShowDeleteModal(true);
  };

  // Confirm deletion
  const confirmDelete = async () => {
    if (!consultationToDelete) return;

    try {
      const response = await authFetch(`${API_BASE_URL}/api/followup-consult/${consultationToDelete.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Refresh the consultations list
        await fetchFollowUpConsultations();
        // Clear selected date consultations
        setSelectedDateConsultations([]);
        // Clear selected date to reset the view
        setSelectedDate(null);
        setShowDeleteModal(false);
        setConsultationToDelete(null);
      } else {
        console.error('Failed to delete consultation');
        alert('Failed to delete consultation. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting consultation:', error);
      alert('Error deleting consultation. Please try again.');
    }
  };

  // Cancel deletion
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setConsultationToDelete(null);
  };

  // Handle edit consultation
  const handleEditConsultation = (consultation) => {
    setConsultationToEdit(consultation);
    setEditDateError(''); // Clear any previous errors
    // Parse the existing date
    const existingDate = new Date(consultation.date);
    const month = String(existingDate.getMonth() + 1).padStart(2, '0');
    const day = String(existingDate.getDate()).padStart(2, '0');
    const year = existingDate.getFullYear();
    
    setEditMonth(month);
    setEditDay(day);
    setEditYear(String(year));
    setEditDate(`${year}-${month}-${day}`);
    setShowEditModal(true);
  };

  // Get days in month for date picker
  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    return new Date(year, month, 0).getDate();
  };

  // Handle date dropdown changes
  const handleEditMonthChange = (e) => {
    const month = e.target.value;
    setEditMonth(month);
    setEditDateError(''); // Clear error when user changes date
    if (month && editDay && editYear) {
      const daysInMonth = getDaysInMonth(month, editYear);
      if (parseInt(editDay) > daysInMonth) {
        setEditDay(String(daysInMonth).padStart(2, '0'));
      }
      setEditDate(`${editYear}-${month}-${editDay}`);
    }
  };

  const handleEditDayChange = (e) => {
    const day = e.target.value;
    setEditDay(day);
    setEditDateError(''); // Clear error when user changes date
    if (editMonth && day && editYear) {
      setEditDate(`${editYear}-${editMonth}-${day}`);
    }
  };

  const handleEditYearChange = (e) => {
    const year = e.target.value;
    setEditYear(year);
    setEditDateError(''); // Clear error when user changes date
    if (editMonth && editDay && year) {
      setEditDate(`${year}-${editMonth}-${editDay}`);
    }
  };

  // Confirm edit
  const confirmEdit = async () => {
    if (!consultationToEdit || !editDate) return;

    // Validate that the date is not in the past
    const newDate = new Date(editDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    newDate.setHours(0, 0, 0, 0);

    if (newDate < today) {
      setEditDateError('Follow-up date cannot be in the past. Please select a current or future date.');
      return;
    }

    // Clear any previous errors
    setEditDateError('');

    try {
      const response = await authFetch(`${API_BASE_URL}/api/followup-consult/${consultationToEdit.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: editDate
        })
      });

      if (response.ok) {
        // Refresh the consultations list
        await fetchFollowUpConsultations();
        // Clear selected date consultations
        setSelectedDateConsultations([]);
        // Clear selected date to reset the view
        setSelectedDate(null);
        setShowEditModal(false);
        setConsultationToEdit(null);
        setEditDate('');
        setEditMonth('');
        setEditDay('');
        setEditYear('');
        setEditDateError('');
      } else {
        console.error('Failed to update consultation');
        setEditDateError('Failed to update consultation. Please try again.');
      }
    } catch (error) {
      console.error('Error updating consultation:', error);
      setEditDateError('Error updating consultation. Please try again.');
    }
  };

  // Cancel edit
  const cancelEdit = () => {
    setShowEditModal(false);
    setConsultationToEdit(null);
    setEditDate('');
    setEditMonth('');
    setEditDay('');
    setEditYear('');
    setEditDateError('');
  };

  return (
    <div className={`${styles.followupContainer} ${!isDarkMode ? styles.lightMode : ''}`}>
      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
              <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <h2 className={styles.sectionTitle}>Follow-up Consultation Calendar</h2>
        </div>

          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading consultations...</p>
            </div>
          ) : (
            <>
          {/* Calendar Container */}
          <div className={styles.calendarContainer}>
            <div className={styles.calendarWrapper}>
              <div className={styles.calendarMainSection}>
                {/* Calendar Header */}
                <div className={styles.calendarHeader}>
          <div className={styles.calendarHeaderLeft}>
            <h3 className={styles.calendarTitle}>
              {currentMonth} {currentYear}
            </h3>
          </div>
          
          <div className={styles.calendarHeaderRight}>
            <button className={styles.btnToday} onClick={handleToday}>
              Today
            </button>
            
            <div className={styles.navButtons}>
              <button 
                className={styles.btnNav}
                onClick={handlePrevMonth}
                title="Previous month"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className={styles.btnNav}
                onClick={handleNextMonth}
                title="Next month"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className={styles.calendarGrid}>
          {/* Day headers */}
          <div className={styles.dayHeaders}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className={styles.dayHeader}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className={styles.daysGrid}>
            {calendarDays.map((dayObj, index) => {
              const consultCount = getConsultationCount(dayObj.date);
              return (
                <div
                  key={index}
                  className={`${styles.dayCell} ${
                    !dayObj.isCurrentMonth ? styles.otherMonth : ''
                  } ${
                    isToday(dayObj.date) ? styles.today : ''
                  } ${
                    isSelected(dayObj.date) ? styles.selected : ''
                  } ${
                    consultCount > 0 ? styles.hasConsultations : ''
                  }`}
                  onClick={() => handleDateClick(dayObj)}
                >
                  <span className={styles.dayNumber}>{dayObj.day}</span>
                  {consultCount > 0 && (
                    <span className={styles.consultBadge}>
                      {consultCount}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
              </div>

              {/* Selected Date Info - Side Panel */}
              <div className={styles.selectedDateInfo}>
                {/* Consultations List */}
                {selectedDate ? (
                  <div className={styles.consultationsList}>
                    <div className={styles.consultationsHeader}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Scheduled Consultations ({selectedDateConsultations.length})</span>
                    </div>
                    
                    <div className={styles.consultationsScroll}>
                      {selectedDateConsultations.length === 0 ? (
                        <div className={styles.noConsultations}>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <p>No consultations scheduled</p>
                          <span>This date has no scheduled follow-up consultations.</span>
                        </div>
                      ) : (
                        selectedDateConsultations.map((consultation, index) => (
                          <div key={index} className={styles.consultationItem}>
                            <div className={styles.consultationIcon}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div className={styles.consultationDetails}>
                              <div className={styles.consultationPatient}>
                                {consultation.hr_info ? 
                                  `${consultation.hr_info.last_name}, ${consultation.hr_info.first_name}` :
                                  `PDL ID: ${consultation.hr_info_id}`
                                }
                              </div>
                              <div className={styles.consultationTime}>
                                {consultation.hr_info?.alias_nickname && (
                                  <span className={styles.consultationAlias}>AKA: {consultation.hr_info.alias_nickname}</span>
                                )}
                              </div>
                              {consultation.hr_info?.cell && (
                                <div className={styles.consultationCell}>
                                  Cell: {consultation.hr_info.cell.cell_no}
                                </div>
                              )}
                            </div>
                            <div className={styles.consultationActions}>
                              <button 
                                className={styles.btnSelectPDL}
                                onClick={() => handleSelectPDL(consultation)}
                                disabled={!consultation.hr_info}
                                title="Select PDL for consultation"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button 
                                className={styles.btnEditConsultation}
                                onClick={() => handleEditConsultation(consultation)}
                                title="Edit follow-up date"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button 
                                className={styles.btnDeleteConsultation}
                                onClick={() => handleDeleteConsultation(consultation)}
                                title="Delete consultation"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={styles.noSelectionMessage}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <p>No date selected</p>
                    <span>Click on a date in the calendar to view scheduled consultations.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
            </>
          )}
      </div>

      {/* PDL Selection Confirmation Modal */}
      {showConfirmModal && selectedPDLForConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmModalContent}>
            <div className={styles.modalHeader}>
              <h3>Confirm PDL Selection</h3>
              <button onClick={cancelSelection} className={styles.closeButton}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.confirmMessage}>
                <p>Are you sure you want to select this PDL for consultation?</p>
              </div>

              {selectedPDLForConfirm.hasUpcomingDateWarning && (
                <div className={styles.deleteWarningMessage}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>
                    This PDL follow-up consultation is scheduled on {formatFollowUpDate(selectedPDLForConfirm.followUpConsultationDate)}. Are you sure you want to process now?
                  </span>
                </div>
              )}
              
              <div className={styles.pdlDetailsCard}>
                <div className={styles.pdlPhotoSection}>
                  <img 
                    src={selectedPDLForConfirm.photo_of_pdl ? `/api/storage/${selectedPDLForConfirm.photo_of_pdl}` : '/default-image.png'}
                    alt="PDL"
                    className={styles.pdlPhoto}
                    onError={(e) => { e.target.onerror = null; e.target.src = '/default-image.png'; }}
                  />
                </div>

                <div className={styles.pdlDetailsGrid}>
                  <div className={styles.pdlDetailItem}>
                    <label>Full Name:</label>
                    <span>
                      {selectedPDLForConfirm.last_name}, {selectedPDLForConfirm.first_name} 
                      {selectedPDLForConfirm.middle_name && ` ${selectedPDLForConfirm.middle_name}`}
                    </span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>AKA / Alias:</label>
                    <span>{selectedPDLForConfirm.alias_nickname || selectedPDLForConfirm.aka || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Sex:</label>
                    <span>{selectedPDLForConfirm.sex === 'M' ? 'Male' : selectedPDLForConfirm.sex === 'F' ? 'Female' : selectedPDLForConfirm.sex || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Age:</label>
                    <span>{calculateAge(selectedPDLForConfirm.date_of_birth)} years old</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Civil Status:</label>
                    <span>{selectedPDLForConfirm.civil_status || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Current Cell:</label>
                    <span>
                      {selectedPDLForConfirm.cell?.cell_no || 'Unassigned'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={styles.confirmActions}>
                <button 
                  onClick={cancelSelection} 
                  className={styles.cancelConfirmButton}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmSelection} 
                  className={styles.confirmSelectButton}
                >
                  Confirm Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selection Success Modal */}
      {showSelectionSuccessModal && (
        <div className={styles.selectionSuccessModalOverlay}>
          <div className={styles.selectionSuccessModalContent}>
            <div className={styles.selectionSuccessModalHeader}>
              <div className={styles.selectionSuccessIcon}>✅</div>
              <h3>PDL Selected Successfully</h3>
            </div>
            <div className={styles.selectionSuccessModalBody}>
              <div className={styles.selectionSuccessMessage}>
                <strong>Selected PDL:</strong> {selectionSuccessData.name}
              </div>
              <div className={styles.selectionSuccessDetails}>
                {selectionSuccessData.message}
              </div>
            </div>
            <div className={styles.selectionSuccessModalActions}>
              <button 
                onClick={handleSuccessModalClose}
                className={styles.btnCloseSelectionSuccess}
              >
                Continue to Consultation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Consultation Modal */}
      {showEditModal && consultationToEdit && (
        <div className={styles.modalOverlay}>
          <div className={styles.editModalContent}>
            <div className={styles.editModalHeader}>
              <div className={styles.editIcon}>📅</div>
              <h3>Edit Follow-up Date</h3>
              <button onClick={cancelEdit} className={styles.closeButton}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.editMessage}>
                <p>Update the follow-up consultation date for:</p>
              </div>
              
              <div className={styles.editPDLInfo}>
                <strong>
                  {consultationToEdit.hr_info ? 
                    `${consultationToEdit.hr_info.last_name}, ${consultationToEdit.hr_info.first_name}` :
                    `PDL ID: ${consultationToEdit.hr_info_id}`
                  }
                </strong>
                {consultationToEdit.hr_info?.alias_nickname && (
                  <span className={styles.editPDLAlias}>AKA: {consultationToEdit.hr_info.alias_nickname}</span>
                )}
              </div>

              <div className={styles.datePickerSection}>
                <label className={styles.datePickerLabel}>New Follow-up Date:</label>
                <div className={styles.dateSelectionGrid}>
                  <div className={styles.dateSelectGroup}>
                    <label>Month</label>
                    <select
                      className={styles.dateSelect}
                      value={editMonth}
                      onChange={handleEditMonthChange}
                    >
                      <option value="">Month</option>
                      <option value="01">January</option>
                      <option value="02">February</option>
                      <option value="03">March</option>
                      <option value="04">April</option>
                      <option value="05">May</option>
                      <option value="06">June</option>
                      <option value="07">July</option>
                      <option value="08">August</option>
                      <option value="09">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </select>
                  </div>
                  <div className={styles.dateSelectGroup}>
                    <label>Day</label>
                    <select
                      className={styles.dateSelect}
                      value={editDay}
                      onChange={handleEditDayChange}
                      disabled={!editMonth}
                    >
                      <option value="">Day</option>
                      {Array.from({ length: getDaysInMonth(editMonth, editYear) }, (_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.dateSelectGroup}>
                    <label>Year</label>
                    <select
                      className={styles.dateSelect}
                      value={editYear}
                      onChange={handleEditYearChange}
                    >
                      <option value="">Year</option>
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() + i;
                        return <option key={year} value={year}>{year}</option>;
                      })}
                    </select>
                  </div>
                </div>
                {editDate && (
                  <div className={styles.selectedDatePreview}>
                    Selected: {new Date(editDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                )}
                {editDateError && (
                  <div className={styles.editDateError}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{editDateError}</span>
                  </div>
                )}
              </div>
              
              <div className={styles.editActions}>
                <button 
                  onClick={cancelEdit} 
                  className={styles.cancelEditButton}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmEdit} 
                  className={styles.confirmEditButton}
                  disabled={!editMonth || !editDay || !editYear}
                >
                  Update Date
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && consultationToDelete && (
        <div className={styles.modalOverlay}>
          <div className={styles.deleteModalContent}>
            <div className={styles.deleteModalHeader}>
              <div className={styles.deleteWarningIcon}>⚠️</div>
              <h3>Delete Consultation</h3>
              <button onClick={cancelDelete} className={styles.closeButton}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.deleteMessage}>
                <p>Are you sure you want to delete this follow-up consultation?</p>
              </div>
              
              <div className={styles.deleteDetailsCard}>
                <div className={styles.deleteDetailItem}>
                  <label>PDL Name:</label>
                  <span>
                    {consultationToDelete.hr_info ? 
                      `${consultationToDelete.hr_info.last_name}, ${consultationToDelete.hr_info.first_name}` :
                      `PDL ID: ${consultationToDelete.hr_info_id}`
                    }
                  </span>
                </div>
                {consultationToDelete.hr_info?.alias_nickname && (
                  <div className={styles.deleteDetailItem}>
                    <label>AKA:</label>
                    <span>{consultationToDelete.hr_info.alias_nickname}</span>
                  </div>
                )}
                <div className={styles.deleteDetailItem}>
                  <label>Follow-up Date:</label>
                  <span>{new Date(consultationToDelete.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
              </div>
              
              <div className={styles.deleteWarningMessage}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>This action cannot be undone.</span>
              </div>
              
              <div className={styles.deleteActions}>
                <button 
                  onClick={cancelDelete} 
                  className={styles.cancelDeleteButton}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDelete} 
                  className={styles.confirmDeleteButton}
                >
                  Delete Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsultFollowup;
